import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router'
import type { ParcelSearchRow, RouterInputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { SectionTitle } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { placeTypeOf } from 'shared/lib/roles'
import { useLocationScope } from 'shared/store/location-scope'
import { ContainerCard } from './ContainerCard'
import { ParcelDetail } from './ParcelDetail'
import { ScanBar } from './ScanBar'
import { elsewhereWords, sum } from './lookup-words'

/**
 * Parcel search and detail — Section 3 of the operator application (spec §12).
 * Staff find a Parcel and read the truth about it: its current custody state,
 * its facts, and its full history read from the Event Log (ADR-0013).
 *
 * The search page is the hub's look: the scan field first — a scanned code opens its
 * Parcel's or its Container's card at once, any other code (a client code, part of a
 * code) is searched by — then one line of search fields for a person, an order, a
 * Point, over the table of what was found. A row opens its card.
 *
 * What a Point operator reads is the server's answer (ticket 48): their own Parcel
 * in full, another Point's only where it is; a search by a person finds only their
 * own. Who may change a Parcel is the card's business (`ParcelDetail`).
 */

type SearchCriteria = RouterInputs['parcels']['Search']

const ANY_POINT = '__any__'

/** The most rows the server answers to a search (ticket 48): a full page asks to narrow it. */
const SEARCH_LIMIT = 200

export function Parcels() {
	const navigate = useNavigate()
	const { parcelId, containerId } = useParams()
	const openParcel = (id: string) => navigate(`/parcels/${encodeURIComponent(id)}`)
	const openContainer = (id: string) => navigate(`/parcels/containers/${encodeURIComponent(id)}`)

	if (containerId) {
		return (
			<ContainerCard containerId={containerId} onBack={() => navigate('/parcels')} onOpenParcel={openParcel} />
		)
	}

	if (parcelId) {
		return <ParcelDetail parcelId={parcelId} onBack={() => navigate('/parcels')} />
	}

	return (
		<PageFrame title='nav.parcels'>
			<ParcelSearch onOpen={openParcel} onOpenContainer={openContainer} />
		</PageFrame>
	)
}

function ParcelSearch({
	onOpen,
	onOpenContainer,
}: {
	onOpen: (parcelId: string) => void
	onOpenContainer: (containerId: string) => void
}) {
	const { t } = useTranslate()
	const [recipient, setRecipient] = useState('')
	const [partnerOrderRef, setPartnerOrderRef] = useState('')
	const [pointFilter, setPointFilter] = useState<string>(ANY_POINT)
	// An operator searches the Point their account is bound to (ADR-0029); an
	// admin chooses a Point, or none; a cashier and support search everywhere.
	const scope = useLocationScope()
	const searchPointId = scope.isBound ? (scope.locationId ?? ANY_POINT) : pointFilter
	const [criteria, setCriteria] = useState<SearchCriteria | null>(null)
	const [warning, setWarning] = useState(false)

	const locationsQuery = useQuery({
		...trpc.locations.List.queryOptions(),
		enabled: scope.canPick,
	})

	const searchQuery = useQuery({
		...trpc.parcels.Search.queryOptions(criteria ?? {}),
		enabled: criteria !== null,
	})
	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		const next: SearchCriteria = {}
		if (recipient.trim()) next.recipient = recipient.trim()
		if (partnerOrderRef.trim()) next.partnerOrderRef = partnerOrderRef.trim()
		// An operator's Point narrows nothing they typed — a code finds a Parcel anywhere
		// in the network, a person only at their Point, by the server (ticket 48); with
		// nothing typed it lists their Point.
		if (searchPointId !== ANY_POINT && (!scope.isBound || Object.keys(next).length === 0)) {
			next.destinationPointId = searchPointId
		}

		if (Object.keys(next).length === 0) {
			setWarning(true)
			setCriteria(null)
			return
		}
		setWarning(false)
		setCriteria(next)
	}

	// A scanned code that is no one Parcel or Container: search by it — a client code
	// lists that customer's Parcels, part of a code the Parcels it fits. An operator's
	// Point narrows no code (the server finds it anywhere).
	const searchByCode = (code: string) => {
		setWarning(false)
		setCriteria(scope.isBound || searchPointId === ANY_POINT ? { trackingCode: code } : { trackingCode: code, destinationPointId: searchPointId })
	}

	const handleReset = () => {
		setRecipient('')
		setPartnerOrderRef('')
		setPointFilter(ANY_POINT)
		setCriteria(null)
		setWarning(false)
	}

	const results: ParcelSearchRow[] = criteria ? (searchQuery.data ?? []) : []
	const searched = criteria !== null && !searchQuery.isFetching

	return (
		<div className='flex flex-col gap-8'>
			<ScanBar
				onOpen={onOpen}
				onOpenContainer={onOpenContainer}
				locationId={searchPointId === ANY_POINT ? undefined : searchPointId}
				onUnresolved={searchByCode}
				hint={t('parcels.searchTrackingCode')}
			/>

			<section className='flex flex-col gap-3'>
				<SectionTitle
					title={t('parcels.title')}
					count={searched ? results.length : undefined}
					hint={scope.isBound ? t('lookup.ownOnlyHint') : undefined}
				/>
				<form className='flex flex-wrap items-end gap-2' onSubmit={handleSubmit} data-testid='parcel-search-form'>
					<SearchField
						id='recipient'
						label={t('parcels.searchRecipient')}
						placeholder={t('parcels.searchRecipientPlaceholder')}
						value={recipient}
						onChange={setRecipient}
					/>
					<SearchField
						id='partnerOrderRef'
						label={t('parcels.searchPartnerOrderRef')}
						placeholder={t('parcels.searchOrderPlaceholder')}
						value={partnerOrderRef}
						onChange={setPartnerOrderRef}
					/>
					{scope.canPick && (
						<div className='w-44'>
							<Label htmlFor='point' className='sr-only'>
								{t('parcels.searchPoint')}
							</Label>
							<Select value={pointFilter} onValueChange={setPointFilter}>
								<SelectTrigger id='point' className='w-full'>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ANY_POINT}>{t('parcels.searchAnyPoint')}</SelectItem>
									{locationsQuery.data
										?.filter((l) => placeTypeOf(l) === 'point')
										.map((location) => (
											<SelectItem key={location._id} value={location._id}>
												{location.id}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
						</div>
					)}
					<Button type='submit' variant='secondary' disabled={searchQuery.isFetching}>
						{t('parcels.searchSubmit')}
					</Button>
					{criteria !== null && (
						<Button type='button' variant='ghost' onClick={handleReset}>
							{t('parcels.searchReset')}
						</Button>
					)}
				</form>
				{warning && <p className='text-sm text-destructive'>{t('parcels.searchNeedCriterion')}</p>}

				<ResultsTable
					rows={results}
					fetching={searchQuery.isFetching}
					emptyText={
						criteria === null
							? t('parcels.searchPrompt')
							: criteria.trackingCode
								? t('parcels.scanNotFound')
								: t('parcels.searchNoResults')
					}
					onOpen={onOpen}
				/>
				{searched && results.length >= SEARCH_LIMIT && (
					<p className='text-sm text-muted-foreground' data-testid='search-limit'>
						{t('lookup.limitNote', { count: String(SEARCH_LIMIT) })}
					</p>
				)}
			</section>
		</div>
	)
}

/** One search field of the line: its words are its label for a reader, the hint is its placeholder. */
function SearchField({
	id,
	label,
	placeholder,
	value,
	onChange,
}: {
	id: string
	label: string
	placeholder: string
	value: string
	onChange: (value: string) => void
}) {
	return (
		<div className='w-56'>
			<Label htmlFor={id} className='sr-only'>
				{label}
			</Label>
			<Input id={id} value={value} placeholder={placeholder} title={label} onChange={(e) => onChange(e.target.value)} />
		</div>
	)
}

/** The Parcels found: one row each, a row opens its card. Another Point's Parcel says only where it is. */
function ResultsTable({
	rows,
	fetching,
	emptyText,
	onOpen,
}: {
	rows: ParcelSearchRow[]
	fetching: boolean
	emptyText: string
	onOpen: (parcelId: string) => void
}) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<ParcelSearchRow>[]>(
		() => [
			{
				id: 'code',
				header: t('parcels.colCode'),
				cell: ({ row }) => (
					<span className='font-mono'>
						{row.original.view === 'full'
							? row.original.resolvingKeys.map((k) => k.trackingCode).join(', ')
							: row.original.trackingCode}
					</span>
				),
			},
			{
				id: 'state',
				header: t('parcels.colState'),
				cell: ({ row }) =>
					row.original.view === 'full' ? (
						<Badge variant='secondary'>{t(`parcelState.${row.original.state}` as never)}</Badge>
					) : (
						<span className='text-muted-foreground'>{elsewhereWords(t, row.original)}</span>
					),
			},
			{
				id: 'recipient',
				header: t('parcels.colRecipient'),
				cell: ({ row }) =>
					row.original.view === 'full' ? (
						<span>
							{row.original.recipient.name}{' '}
							<span className='text-muted-foreground'>{row.original.recipient.clientRef}</span>
						</span>
					) : null,
			},
			{
				id: 'point',
				header: t('parcels.colPoint'),
				cell: ({ row }) => (row.original.view === 'full' ? row.original.destinationPointCode : null),
			},
			{
				id: 'order',
				header: t('parcels.colOrder'),
				cell: ({ row }) =>
					row.original.view === 'full' ? (
						<span className='text-muted-foreground'>{row.original.partnerOrderRef}</span>
					) : null,
			},
			{
				id: 'amount',
				header: t('parcels.colAmount'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row }) => (row.original.view === 'full' ? sum(row.original.amountOwed) : null),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row }) => (
					<Button
						variant='ghost'
						size='sm'
						onClick={(event) => {
							event.stopPropagation()
							onOpen(row.original.parcelId)
						}}
					>
						{t('parcels.cardOpen')}
					</Button>
				),
			},
		],
		[t, onOpen],
	)
	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (p) => p.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: SEARCH_LIMIT } },
	})
	return (
		<DataTable
			table={table}
			withPagination={false}
			isFetching={fetching}
			isRefetching={fetching}
			emptyText={<span data-testid='search-empty'>{fetching ? t('parcels.searching') : emptyText}</span>}
			row={{
				onRowClick: (p) => onOpen(p.parcelId),
				attributes: (p) => ({ 'data-testid': p.view === 'full' ? 'search-row' : 'search-elsewhere' }),
			}}
		/>
	)
}
